import axios from 'axios';

const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// ========================================
// INTERCEPTOR PARA CLERK JWT TOKEN
// ========================================

/**
 * Rutas públicas que NO requieren autenticación JWT ni prefijo de tenant
 */
const PUBLIC_ROUTES = [
    '/firmar',         // Rutas de firma de contratos con token
    '/public/tenants', // Branding público por tenant code
];

/**
 * Rutas administrativas que no llevan prefijo de tenant (solo superadmin)
 */
const ADMIN_ROUTES = [
    '/admin',
    '/users',
];

/**
 * Prefijos de rutas de negocio que se deben prefijar con /:tenantCode/
 */
const TENANT_ROUTE_PREFIXES = [
    '/clients',
    '/subscriptions',
    '/client-subscription',
    '/configurations',
    '/payments',
    '/sepa',
    '/eventos',
    '/niveles',
    '/instructores',
    '/classrooms',
    '/festivos',
    '/plantillas-semanales',
    '/plazas',
    // D2: sin esto las peticiones salían a /api/prospects, sin el código de
    // centro, y la API responde 404.
    '/prospects',
    '/inscripciones',
    '/contratos',
    '/audit-logs',
    // P6 fase 2: mismo fallo que con `/prospects` en D2 — sin esto las peticiones
    // del instructor salían a /api/teaching/..., sin el código de centro, y la
    // API responde 404.
    '/teaching',
];

const getActiveTenantCodeFromPathname = (): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const [firstSegment] = window.location.pathname.split('/').filter(Boolean);

    if (!firstSegment || ['admin', 'login', 'firmar'].includes(firstSegment)) {
        return null;
    }

    return firstSegment;
};

/**
 * Verifica si una URL es de una ruta pública
 */
const isPublicRoute = (url?: string): boolean => {
    if (!url) return false;
    return PUBLIC_ROUTES.some(route => url.startsWith(route));
};

/**
 * Verifica si una URL ya tiene prefijo de tenant (evitar doble prefijado).
 *
 * Formato: /TENANTCODE/recurso — el tenantCode es alfanumérico con guiones.
 *
 * El `(?=\/|$|\?)` final es imprescindible: sin él, el nombre del recurso podía
 * casar a medias y la URL parecía ya prefijada cuando no lo estaba. El caso real
 * era `/sepa-workflow/sepa-transaction`, donde `sepa-workflow` se leía como
 * tenantCode y `sepa-transaction` casaba con la alternativa `sepa` — así que no
 * se prefijaba y la petición salía a `/api/sepa-workflow/...`, que no existe
 * (el backend monta `/:tenantCode/sepa-workflow`). Devolvía 404.
 *
 * La lista se deriva de TENANT_ROUTE_PREFIXES para que no puedan divergir: antes
 * estaban duplicadas a mano, y añadir un prefijo en una y olvidarlo en la otra
 * reintroduce este mismo fallo.
 */
const TENANT_PREFIX_REGEX = new RegExp(
    `^/[a-zA-Z0-9_-]+/(${TENANT_ROUTE_PREFIXES
        .map(prefix => prefix.slice(1))
        // Más largos primero: evita que una alternativa corta (`clients`) tape a
        // otra que la contiene (`client-subscription`).
        .sort((a, b) => b.length - a.length)
        .join('|')})(?=/|$|\\?)`
);

const hasTenantPrefix = (url: string): boolean => TENANT_PREFIX_REGEX.test(url);

/**
 * Verifica si una URL requiere prefijo de tenant
 */
const isTenantRoute = (url?: string): boolean => {
    if (!url) return false;
    if (isPublicRoute(url)) return false;
    if (ADMIN_ROUTES.some(r => url.startsWith(r))) return false;
    if (hasTenantPrefix(url)) return false;
    return TENANT_ROUTE_PREFIXES.some(prefix => url.startsWith(prefix));
};

// Función para obtener el token y tenantCode de Clerk de forma segura
const getClerkAuth = async (): Promise<{ token: string | null; tenantCode: string | null }> => {
    try {
        const clerk = (window as any).Clerk;
        const activeTenantCode = getActiveTenantCodeFromPathname();
        if (clerk?.session) {
            const token = await clerk.session.getToken();
            const tenantCode = activeTenantCode || (clerk.user?.publicMetadata?.tenantCode as string) || null;
            return { token, tenantCode };
        }
        return { token: null, tenantCode: activeTenantCode };
    } catch (error) {
        console.warn('Error obteniendo auth de Clerk:', error);
        return { token: null, tenantCode: getActiveTenantCodeFromPathname() };
    }
};

// Agregar token automáticamente a todas las requests (excepto rutas públicas)
apiClient.interceptors.request.use(
    async (config) => {
        // No agregar JWT ni tenant prefix para rutas públicas
        if (isPublicRoute(config.url)) {
            return config;
        }

        const { token, tenantCode } = await getClerkAuth();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (tenantCode) {
            config.headers['X-Active-Tenant-Code'] = tenantCode;
        }

        // Prefijar rutas de negocio con /:tenantCode/
        if (tenantCode && config.url && isTenantRoute(config.url)) {
            config.url = `/${tenantCode}${config.url}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor para manejar errores de autenticación
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Error de autenticación - Token inválido o expirado');
            // Aquí podrías redirigir al login si es necesario
            // window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export default apiClient;